/**
 * Simple image fallback utilities for better reliability
 */

import { OptimizedImageObject } from "@/types/image.types";

export interface ImageFallbackOptions {
  preferredSize?: "mobile" | "tablet" | "desktop" | "large";
  preferWebp?: boolean;
  maxRetries?: number;
}

/**
 * Get fallback URLs in order of preference
 */
export function getFallbackUrls(
  imageSource: OptimizedImageObject | string,
  options: ImageFallbackOptions = {},
): string[] {
  if (typeof imageSource === "string") {
    return [imageSource];
  }

  const { preferredSize = "desktop", preferWebp = false } = options;
  const fallbackUrls: string[] = [];

  // Add preferred size first (WebP if preferred)
  if (preferWebp) {
    switch (preferredSize) {
      case "mobile":
        if (imageSource.mobileWebp) fallbackUrls.push(imageSource.mobileWebp);
        break;
      case "tablet":
        if (imageSource.tabletWebp) fallbackUrls.push(imageSource.tabletWebp);
        break;
      case "desktop":
        if (imageSource.desktopWebp) fallbackUrls.push(imageSource.desktopWebp);
        break;
      case "large":
        if (imageSource.largeWebp) fallbackUrls.push(imageSource.largeWebp);
        break;
    }
  }

  // Add non-WebP preferred size
  switch (preferredSize) {
    case "mobile":
      if (imageSource.mobile) fallbackUrls.push(imageSource.mobile);
      break;
    case "tablet":
      if (imageSource.tablet) fallbackUrls.push(imageSource.tablet);
      break;
    case "desktop":
      if (imageSource.desktop) fallbackUrls.push(imageSource.desktop);
      break;
    case "large":
      if (imageSource.large) fallbackUrls.push(imageSource.large);
      break;
  }

  // For card images (tablet preferred), add smart fallback order
  if (preferredSize === "tablet") {
    // Try tablet -> desktop -> mobile -> large -> original
    const orderedSizes = [
      imageSource.tablet,
      imageSource.desktop,
      imageSource.mobile,
      imageSource.large,
      imageSource.original,
    ];

    orderedSizes.forEach((url) => {
      if (url && !fallbackUrls.includes(url)) {
        fallbackUrls.push(url);
      }
    });
  } else {
    // Default fallback order: larger to smaller, then original
    const allSizes = [
      imageSource.large,
      imageSource.desktop,
      imageSource.tablet,
      imageSource.mobile,
      imageSource.original,
    ];

    allSizes.forEach((url) => {
      if (url && !fallbackUrls.includes(url)) {
        fallbackUrls.push(url);
      }
    });
  }

  return fallbackUrls.filter(Boolean);
}

/**
 * Simple image URL validator - checks if URL structure looks valid
 */
export function isValidImageUrl(url: string): boolean {
  if (!url || typeof url !== "string") return false;

  // Basic checks
  const hasValidProtocol =
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("blob:");
  const hasImageExtension = /\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i.test(url);
  const hasValidDomain =
    url.includes("storage.googleapis.com") ||
    url.includes("avezor") ||
    url.startsWith("blob:");

  return (
    hasValidProtocol &&
    (hasImageExtension || url.startsWith("blob:")) &&
    hasValidDomain
  );
}

/**
 * Check if image URL is from GCS and is in optimized format
 */
export function isOptimizedGcsUrl(url: string): boolean {
  if (!url || typeof url !== "string") return false;

  return (
    url.includes("storage.googleapis.com/avezor-images") &&
    (url.includes("/320/") ||
      url.includes("/640/") ||
      url.includes("/1280/") ||
      url.includes("/1920/") ||
      url.includes("/original/"))
  );
}

/**
 * Extract the original filename from an optimized GCS URL
 */
export function extractOriginalFilename(url: string): string | null {
  if (!isOptimizedGcsUrl(url)) return null;

  const match = url.match(/\/(?:320|640|1280|1920|original)\/([^?]+)/);
  return match ? match[1] : null;
}

/**
 * Create fallback URL by replacing the size in the path
 */
export function createSizeFallbackUrl(
  url: string,
  targetSize: "320" | "640" | "1280" | "1920" | "original",
): string | null {
  if (!isOptimizedGcsUrl(url)) return null;

  return url.replace(/\/(320|640|1280|1920|original)\//, `/${targetSize}/`);
}

/**
 * Get smart fallback URL based on failed URL
 */
export function getSmartFallbackUrl(failedUrl: string): string | null {
  if (!isOptimizedGcsUrl(failedUrl)) return null;

  // Try fallback sizes in order: 1920 -> 1280 -> 640 -> 320 -> original
  if (failedUrl.includes("/1920/")) {
    return createSizeFallbackUrl(failedUrl, "1280");
  }
  if (failedUrl.includes("/1280/")) {
    return createSizeFallbackUrl(failedUrl, "640");
  }
  if (failedUrl.includes("/640/")) {
    return createSizeFallbackUrl(failedUrl, "320");
  }
  if (failedUrl.includes("/320/")) {
    return createSizeFallbackUrl(failedUrl, "original");
  }

  return null;
}

/**
 * Simple image preloader to check if image exists
 */
export function preloadImage(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
}

/**
 * Check multiple URLs and return the first working one
 */
export async function findWorkingImageUrl(
  urls: string[],
): Promise<string | null> {
  for (const url of urls) {
    if (isValidImageUrl(url)) {
      const works = await preloadImage(url);
      if (works) return url;
    }
  }
  return null;
}

const imageFallbackUtils = {
  getFallbackUrls,
  isValidImageUrl,
  isOptimizedGcsUrl,
  extractOriginalFilename,
  createSizeFallbackUrl,
  getSmartFallbackUrl,
  preloadImage,
  findWorkingImageUrl,
};

export default imageFallbackUtils;
